import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Radio, Space, Row, Col, Select, Tag, message, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetQaqcPipeWeldingdataLog } from '../../../../../hooks/genericHooks';
import type { QaqcPipeWeldingdataLog } from '../../../../../types';
import { fetchSqlDataWithQuery } from '../../../../../services/api';
import { fetchSqlQueryStreaming } from '../../../../../services/api';
import { buildInspStatusQuery, type InspType } from './qaqcPipeWeldInspCheck.query';
import StandardTable from '../../../../../components/common/StandardTable';
import Button from '../../../../../components/common/Button';

const inspTypes: InspType[] = ['BC','VC','PT','RT','MT','UT','PMI','PWHT'];

type RowT = {
  key: string;
  weldingdata_id: string;
  weld_no_rev: number | null | string;
  line_no?: string;
  section?: string;
  serviceclass_name?: string;
  fluid?: string;
  weldtype_name?: string;
  pipesize_name?: string;
  spool_id?: string | number | null;
  wd?: boolean;
  required: boolean;
  done: boolean;
  statusText: string;
};

const QaqcPipeWeldInspCheck: React.FC = () => {
  const [inspType, setInspType] = useState<InspType | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'all' | 'rd' | 'rp' | 'nd' | 'nn'>('all');
  const [rows, setRows] = useState<RowT[]>([]);
  const [loading, setLoading] = useState(false);
  const [promptOpen, setPromptOpen] = useState<boolean>(false);
  const [promptSelection, setPromptSelection] = useState<InspType | undefined>(undefined);
  const controllerRef = useRef<{ abort: () => void; ready?: () => void } | null>(null);

  const { data: logResp } = useGetQaqcPipeWeldingdataLog() as any;
  const weldingLogList: QaqcPipeWeldingdataLog[] = (logResp as any)?.data ?? (logResp as any) ?? [];

  const [filters, setFilters] = useState<{
    section?: string[];
    serviceclass_name?: string[];
    line_no?: string[];
    pipesize_name?: string[];
    weldtype_name?: string[];
    fluid?: string[];
    spool_id?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdataLog)[] = ['fluid','section','line_no','spool_id','serviceclass_name','pipesize_name','weldtype_name'];
  const applyLogFilters = (dataIn: QaqcPipeWeldingdataLog[], excludeField?: keyof QaqcPipeWeldingdataLog) => {
    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '').toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cell.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).toLowerCase() === cell);
          if (!matched) return false;
        }
      }
      return true;
    });
  };
  const buildOptions = (field: keyof QaqcPipeWeldingdataLog) => {
    const base = applyLogFilters(weldingLogList, field);
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  const fetchData = () => {
    if (!inspType) { setRows([]); return; }
    try { controllerRef.current?.abort(); } catch {}
    controllerRef.current = null;
    setRows([]);
    setLoading(true);
    const q = buildInspStatusQuery({
      inspType,
      section: filters.section,
      serviceclass_name: filters.serviceclass_name,
      line_no: filters.line_no,
      pipesize_name: filters.pipesize_name,
      weldtype_name: filters.weldtype_name,
      fluid: filters.fluid,
    });
    const controller = fetchSqlQueryStreaming({
      query_string: q,
      batchSize: 20000,
      flushMs: 240,
      flowControl: true,
      onBatch: (batch: any[]) => {
        const mapped: RowT[] = batch.map((r: any) => ({
          key: `${String(r.weldingdata_id)}|${String(r.weld_no_rev)}`,
          weldingdata_id: String(r.weldingdata_id),
          weld_no_rev: r.weld_no_rev ?? null,
          line_no: r.line_no,
          section: r.section,
          serviceclass_name: r.serviceclass_name,
          fluid: r.fluid,
          weldtype_name: r.weldtype_name,
          pipesize_name: r.pipesize_name,
          spool_id: r.spool_id,
          wd: !!r.wd,
          required: !!r.required,
          done: !!r.done,
          statusText: (!!r.required && !!r.done)
            ? 'Required • Done'
            : (!!r.required && !r.done)
              ? 'Required • Pending'
              : (!r.required && !!r.done)
                ? 'Not Required • Done'
                : 'Not Required • Not Done',
        }));
        if (mapped.length) setRows(prev => prev.concat(mapped));
        try { controller.ready?.(); } catch {}
      },
      onDone: () => setLoading(false),
      onError: () => { setLoading(false); message.error('Failed to load data'); },
    });
    controllerRef.current = controller;
    try { controller.ready?.(); } catch {}
  };

  useEffect(() => {
    if (!inspType) return;
    fetchData();
    return () => { try { controllerRef.current?.abort(); } catch {} };
    /* eslint-disable-next-line */
  }, [inspType, filters]);

  useEffect(() => {
    // Prompt user to select inspection type on initial mount only
    setPromptOpen(true);
  }, []);

  const columns: ColumnsType<RowT> = [
    { title: 'Welding Data ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 220 },
    { title: 'Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 60 },
    { title: 'WD', dataIndex: 'wd', key: 'wd', width: 50, render: (_, r) => (r as any).wd ? '✓' : '' },
    { title: 'Line No', dataIndex: 'line_no', key: 'line_no',width:110 },
    { title: 'Section', dataIndex: 'section', key: 'section', width: 80 },
    { title: 'S.Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 80 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 60 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
    { title: 'W.Type', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 80 },
    { title: 'P.Size', dataIndex: 'pipesize_name', key: 'pipesize_name', width: 70 },
    { title: 'Status', dataIndex: 'statusText', key: 'status', width: 160 },
  ];

  const filteredRows = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter(r => {
      const required = r.required;
      const done = r.done;
      if (statusFilter === 'rd') return required && done;
      if (statusFilter === 'rp') return required && !done;
      if (statusFilter === 'nd') return !required && done;
      if (statusFilter === 'nn') return !required && !done;
      return true;
    });
  }, [rows, statusFilter]);

  return (
    <Card title="Weld Inspection Check" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Row gutter={[8,8]}>
          <Col span={24}>
            <Space style={{ width: '100%' }}>
              <b>Inspection Type:</b>
              <Radio.Group value={inspType} onChange={(e) => setInspType(e.target.value)} size="small" className="insp-type-group">
                {inspTypes.map(t => (<Radio.Button key={t} value={t}>{t}</Radio.Button>))}
              </Radio.Group>
              <Radio.Group
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                style={{ whiteSpace: 'nowrap' }}
              >
                <Radio.Button value="all" title="All">
                  <span className="status-ring" />
                </Radio.Button>
                <Radio.Button value="rd" title="Required • Done">
                  <span className="status-dot green" />
                </Radio.Button>
                <Radio.Button value="rp" title="Required • Pending">
                  <span className="status-dot red" />
                </Radio.Button>
                <Radio.Button value="nd" title="Not Required • Done">
                  <span className="status-dot blue" />
                </Radio.Button>
                <Radio.Button value="nn" title="Not Required • Not Done">
                  <span className="status-dot yellow" />
                </Radio.Button>
              </Radio.Group>
              <span style={{ marginLeft: 'auto' }} />
              <Button size="small" onClick={() => { setFilters({}); setStatusFilter('all'); }}>Reset</Button>
              <span style={{ opacity: 0.75 }}>Total: {filteredRows.length}</span>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('section')} placeholder="Area (Section)" value={filters.section}
                    onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('serviceclass_name')} placeholder="S.Class" value={filters.serviceclass_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={2}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('fluid')} placeholder="Fluid" value={filters.fluid}
                    onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={4}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('line_no')} placeholder="Line No" value={filters.line_no}
                    onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={2}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('pipesize_name')} placeholder="P.Size" value={filters.pipesize_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, pipesize_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={2}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('weldtype_name')} placeholder="W.Type" value={filters.weldtype_name}
                    onChange={(v) => setFilters(prev => ({ ...prev, weldtype_name: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select mode="multiple" allowClear showSearch options={buildOptions('spool_id')} placeholder="Spool" value={filters.spool_id}
                    onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))} style={{ width: '100%' }} />
          </Col>
        </Row>

        <StandardTable<RowT>
          columns={columns as any}
          dataSource={filteredRows}
          rowKey={(r) => r.key}
          loading={loading}
          style={{ width: '100%' }}
          scroll={{ x: 'max-content' }}
          className={'compact-table left-align-table single-line-table'}
          rowClassName={(r) => {
            if (r.required && r.done) return 'row-green';
            if (r.required && !r.done) return 'row-red';
            if (!r.required && r.done) return 'row-blue';
            return 'row-yellow';
          }}
          pagination={{ pageSize: 20 }}
        />
        <Modal
          title="Select Inspection Type"
          open={promptOpen}
          onOk={() => { if (promptSelection) { setInspType(promptSelection); setPromptOpen(false); } }}
          okButtonProps={{ disabled: !promptSelection }}
          onCancel={() => { /* keep prompt open until a selection is made */ }}
          closable={false}
          maskClosable={false}
          destroyOnClose
        >
          <Space direction="vertical">
            <div>Please choose the inspection type to fetch:</div>
            <Radio.Group value={promptSelection} onChange={(e) => setPromptSelection(e.target.value)}>
              {inspTypes.map(t => (<Radio key={t} value={t}>{t}</Radio>))}
            </Radio.Group>
          </Space>
        </Modal>
        <style>{`
          .row-green td { background-color: #f6ffed !important; }
          .row-red td { background-color: #fff1f0 !important; }
          .row-blue td { background-color: #e6f4ff !important; }
          .row-yellow td { background-color: #fffbe6 !important; }
          .status-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
          .status-ring { display: inline-block; width: 10px; height: 10px; border-radius: 50%; border: 1px solid #8c8c8c; background-color: transparent; }
          .status-dot.neutral { background-color: #8c8c8c; }
          .status-dot.green { background-color: #52c41a; }
          .status-dot.red { background-color: #ff4d4f; }
          .status-dot.blue { background-color: #1677ff; }
          .status-dot.yellow { background-color: #fffbe6; border: 1px solid #faad14; }
          .insp-type-group .ant-radio-button-wrapper { padding: 0 6px; min-width: auto; font-size: 12px; }
        `}</style>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Tag color="green">Required • Done</Tag>
          <Tag color="red">Required • Pending</Tag>
          <Tag color="blue">Not Required • Done</Tag>
          <Tag style={{ backgroundColor: '#fffbe6', borderColor: '#faad14', color: '#000' }}>Not Required • Not Done</Tag>
        </div>
      </Space>
    </Card>
  );
};

export default QaqcPipeWeldInspCheck;
